import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import moment from 'moment'
import { validateTimeBands, getTotalHours } from './work-hour-settings-page'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
}
const DAY_SHORT = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
}
const TIME_TYPES = [
  { value: 'STANDARD', key: 'Standard' },
  { value: 'OVERTIME_1', key: 'Overtime' },
  { value: 'PUBLIC_HOLIDAY', key: 'Holiday' },
]

/**
 * Returns human-readable label for a time period enum type.
 * @param {string} time_type - STANDARD, OVERTIME_1, or PUBLIC_HOLIDAY
 * @returns {string} Human-readable label
 */
function formatTypeLabel(time_type) {
  return TIME_TYPES.find((t) => t.value === time_type)?.key || time_type
}

/**
 * CopyDayModal Component
 * Allows copying time periods from any day/exception across settings to target days.
 */
class CopyDayModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = this.getInitialState(props)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.setState(this.getInitialState(this.props))
    }
  }

  getInitialState = (props) => {
    const { targetContext = 'WEEKDAY', selectedDay, weekdayRules = {}, exceptions = [], selectedExcIdx } = props
    let sourceType = 'WEEKDAY'
    let sourceDay = 'monday'
    let weekdaySourceDay = selectedDay || 'monday'
    let targetDays = []

    if (targetContext === 'WEEKDAY') {
      sourceType = 'WEEKDAY'
      sourceDay = selectedDay || 'monday'
      if ((weekdayRules[sourceDay] || []).length === 0) {
        const firstWithPeriods = DAYS.find((d) => (weekdayRules[d] || []).length > 0)
        if (firstWithPeriods) sourceDay = firstWithPeriods
      }
      weekdaySourceDay = sourceDay
      targetDays = []
    } else {
      if (exceptions.length > 0) {
        sourceType = 'EXCEPTION'
        if (selectedExcIdx !== null && exceptions[selectedExcIdx]) {
          sourceDay = `exc_${selectedExcIdx}`
        } else {
          sourceDay = 'exc_0'
        }
      } else {
        sourceType = 'WEEKDAY'
        sourceDay = selectedDay || 'monday'
      }
      weekdaySourceDay = selectedDay || 'monday'
      targetDays = []
    }

    const rawPeriods = this.getSourcePeriods('__CURRENT__', sourceDay, props).rawPeriods
    const allIndices = rawPeriods.map((_, i) => i)

    return {
      copySourceSettingId: '__CURRENT__',
      copySourceType: sourceType,
      copySourceDay: sourceDay,
      copyWeekdaySourceDay: weekdaySourceDay,
      copySelectedPeriodIndices: allIndices,
      copyTargetDays: targetDays,
      copyMode: 'REPLACE',
    }
  }

  getSourcePeriods = (settingId, dayKey, customProps) => {
    const props = customProps || this.props
    const { timePeriodType, weekdayRules = {}, exceptions = [], settings = [] } = props
    let rawPeriods = []
    let sourcePeriodType = 'TIME_PERIOD'

    if (settingId === '__CURRENT__') {
      sourcePeriodType = timePeriodType || 'TIME_PERIOD'
      if (DAYS.includes(dayKey)) {
        rawPeriods = weekdayRules[dayKey] || []
      } else if (dayKey.startsWith('exc_')) {
        const idx = parseInt(dayKey.replace('exc_', ''), 10)
        rawPeriods = exceptions[idx]?.time_periods || []
      }
    } else {
      const s = settings.find((item) => item.id === settingId)
      if (s) {
        sourcePeriodType = s.time_period_type || 'TIME_PERIOD'
        if (DAYS.includes(dayKey)) {
          rawPeriods = s.weekday_rules?.[dayKey] || []
        } else if (dayKey.startsWith('exc_')) {
          const idx = parseInt(dayKey.replace('exc_', ''), 10)
          const exc = (s.date_exceptions || [])[idx]
          rawPeriods = exc?.time_periods || exc?.rules || []
        }
      }
    }
    return { rawPeriods, sourcePeriodType }
  }

  getCopyConflictWarnings = () => {
    const { copyMode, copyTargetDays, copySourceSettingId, copySourceDay, copySelectedPeriodIndices } = this.state
    const { targetContext, timePeriodType, weekdayRules = {}, eTimePeriods = [] } = this.props

    const { rawPeriods } = this.getSourcePeriods(copySourceSettingId, copySourceDay)
    const selectedPeriods = rawPeriods.filter((_, i) => copySelectedPeriodIndices.includes(i))
    if (selectedPeriods.length === 0) return []

    const conflicts = []
    const isHourly = timePeriodType === 'HOURLY'

    const selfTotal = getTotalHours(selectedPeriods)
    if (selfTotal > 24) {
      conflicts.push(`Selected periods total (${selfTotal}h) exceeds 24 hours in a day`)
      return conflicts
    }
    if (!isHourly) {
      const selfErr = validateTimeBands(selectedPeriods)
      if (selfErr) {
        conflicts.push(`Selected periods: ${selfErr}`)
        return conflicts
      }
    }

    if (copyMode !== 'APPEND') return conflicts

    const checkMerged = (existing, label) => {
      if (existing.length === 0) return
      const merged = [...existing, ...selectedPeriods]
      if (isHourly) {
        const total = getTotalHours(merged)
        if (total > 24) {
          conflicts.push(`${label}: Total hours (${total}h) exceeds 24 hours in a day`)
        }
      } else {
        const err = validateTimeBands(merged)
        if (err) {
          conflicts.push(`${label}: ${err}`)
        } else {
          const total = getTotalHours(merged)
          if (total > 24) {
            conflicts.push(`${label}: Total hours (${total}h) exceeds 24 hours in a day`)
          }
        }
      }
    }

    if (targetContext === 'WEEKDAY') {
      copyTargetDays.forEach((tDay) => {
        checkMerged(weekdayRules[tDay] || [], DAY_LABELS[tDay])
      })
    } else {
      checkMerged(eTimePeriods, 'Exception')
    }
    return conflicts
  }

  clonePeriodsForTarget = (rawPeriods, targetType) => {
    return rawPeriods.map((p, i) => {
      let hours = p.hours !== undefined ? p.hours : ''
      let start = p.start || ''
      let end = p.end || ''

      const base = {
        time_type: p.time_type || 'STANDARD',
        multiplier: p.multiplier !== undefined ? Number(p.multiplier) : 1.0,
        unpaid_allowance: p.unpaid_allowance !== undefined ? Number(p.unpaid_allowance) : 0,
        order: i,
      }

      if (targetType === 'HOURLY') {
        if ((!hours || Number(hours) <= 0) && start && end) {
          const [sh, sm] = start.split(':').map(Number)
          const [eh, em] = end.split(':').map(Number)
          const diffHours = (eh * 60 + em - (sh * 60 + sm)) / 60
          hours = diffHours > 0 ? diffHours : 8
        }
        return {
          ...base,
          hours: Number(hours) > 0 ? Number(hours) : 8,
        }
      }

      if ((!start || !end) && Number(hours) > 0) {
        start = '08:00'
        const totalMinutes = 8 * 60 + Math.round(Number(hours) * 60)
        const endH = Math.min(24, Math.floor(totalMinutes / 60)).toString().padStart(2, '0')
        const endM = (totalMinutes % 60).toString().padStart(2, '0')
        end = `${endH}:${endM}`
      }

      return {
        ...base,
        start: start || '08:00',
        end: end || '17:00',
      }
    })
  }

  changeCopySourceType = (newType) => {
    const { copySourceSettingId, copyWeekdaySourceDay } = this.state
    const { targetContext, settings = [], exceptions = [] } = this.props
    let newSourceDay = 'monday'
    if (newType === 'EXCEPTION') {
      const excList = copySourceSettingId === '__CURRENT__'
        ? exceptions
        : (settings.find((s) => s.id === copySourceSettingId)?.date_exceptions || [])
      newSourceDay = excList.length > 0 ? 'exc_0' : 'monday'
    } else {
      newSourceDay = copyWeekdaySourceDay || 'monday'
    }
    const { rawPeriods } = this.getSourcePeriods(copySourceSettingId, newSourceDay)
    this.setState((prev) => ({
      copySourceType: newType,
      copySourceDay: newSourceDay,
      copySelectedPeriodIndices: rawPeriods.map((_, i) => i),
      copyTargetDays: targetContext === 'WEEKDAY'
        ? prev.copyTargetDays.filter((d) => d !== prev.copyWeekdaySourceDay)
        : prev.copyTargetDays,
    }))
  }

  changeCopySourceSetting = (newSettingId) => {
    const { copySourceType, copyWeekdaySourceDay } = this.state
    const { targetContext, settings = [], exceptions = [] } = this.props
    let newSourceDay = 'monday'
    let effectiveType = copySourceType
    if (copySourceType === 'EXCEPTION') {
      const excList = newSettingId === '__CURRENT__'
        ? exceptions
        : (settings.find((s) => s.id === newSettingId)?.date_exceptions || [])
      if (excList.length === 0) {
        effectiveType = 'WEEKDAY'
        newSourceDay = copyWeekdaySourceDay || 'monday'
      } else {
        newSourceDay = 'exc_0'
      }
    } else {
      newSourceDay = copyWeekdaySourceDay || 'monday'
    }
    const { rawPeriods } = this.getSourcePeriods(newSettingId, newSourceDay)
    this.setState((prev) => ({
      copySourceSettingId: newSettingId,
      copySourceType: effectiveType,
      copySourceDay: newSourceDay,
      copySelectedPeriodIndices: rawPeriods.map((_, i) => i),
      copyTargetDays: targetContext === 'WEEKDAY'
        ? prev.copyTargetDays.filter((d) => d !== prev.copyWeekdaySourceDay)
        : prev.copyTargetDays,
    }))
  }

  changeCopySourceDay = (newSourceDay) => {
    const { copySourceSettingId } = this.state
    const { targetContext } = this.props
    const { rawPeriods } = this.getSourcePeriods(copySourceSettingId, newSourceDay)
    this.setState((prev) => {
      let updatedTargets = prev.copyTargetDays
      let newWeekdaySource = prev.copyWeekdaySourceDay
      if (targetContext === 'WEEKDAY') {
        if (prev.copySourceType === 'WEEKDAY' || DAYS.includes(newSourceDay)) {
          newWeekdaySource = newSourceDay
          const withoutNew = prev.copyTargetDays.filter((d) => d !== newSourceDay)
          updatedTargets = withoutNew.includes(prev.copyWeekdaySourceDay) ? withoutNew : [...withoutNew, prev.copyWeekdaySourceDay]
        } else {
          updatedTargets = prev.copyTargetDays.filter((d) => d !== prev.copyWeekdaySourceDay)
        }
      }
      return {
        copySourceDay: newSourceDay,
        copyWeekdaySourceDay: newWeekdaySource,
        copySelectedPeriodIndices: rawPeriods.map((_, i) => i),
        copyTargetDays: updatedTargets,
      }
    })
  }

  toggleCopyPeriodIndex = (index) => {
    this.setState((prev) => {
      const exists = prev.copySelectedPeriodIndices.includes(index)
      const next = exists
        ? prev.copySelectedPeriodIndices.filter((i) => i !== index)
        : [...prev.copySelectedPeriodIndices, index]
      return { copySelectedPeriodIndices: next }
    })
  }

  toggleAllCopyPeriods = (totalCount) => {
    this.setState((prev) => {
      if (prev.copySelectedPeriodIndices.length === totalCount) {
        return { copySelectedPeriodIndices: [] }
      }
      return { copySelectedPeriodIndices: Array.from({ length: totalCount }, (_, i) => i) }
    })
  }

  toggleCopyTargetDay = (day) => {
    const { copySourceSettingId, copyWeekdaySourceDay } = this.state
    if (copySourceSettingId === '__CURRENT__' && copyWeekdaySourceDay === day) {
      return
    }
    this.setState((prev) => {
      const exists = prev.copyTargetDays.includes(day)
      const next = exists
        ? prev.copyTargetDays.filter((d) => d !== day)
        : [...prev.copyTargetDays, day]
      return { copyTargetDays: next }
    })
  }

  selectAllTargetWeekdays = () => {
    const { copyWeekdaySourceDay, copySourceSettingId } = this.state
    const isCurrentSetting = copySourceSettingId === '__CURRENT__'
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].filter((d) =>
      !isCurrentSetting || d !== copyWeekdaySourceDay
    )
    this.setState({ copyTargetDays: weekdays })
  }

  selectAllTargetDays = () => {
    const { copyWeekdaySourceDay, copySourceSettingId } = this.state
    const isCurrentSetting = copySourceSettingId === '__CURRENT__'
    const allDays = DAYS.filter((d) => !isCurrentSetting || d !== copyWeekdaySourceDay)
    this.setState({ copyTargetDays: allDays })
  }

  clearAllTargetDays = () => {
    this.setState({ copyTargetDays: [] })
  }

  handleConfirm = () => {
    const { copySourceSettingId, copySourceDay, copySelectedPeriodIndices, copyTargetDays, copyMode } = this.state
    const { targetContext, timePeriodType, onConfirm } = this.props
    const { rawPeriods } = this.getSourcePeriods(copySourceSettingId, copySourceDay)
    const selectedPeriods = rawPeriods.filter((_, i) => copySelectedPeriodIndices.includes(i))
    if (selectedPeriods.length === 0) return

    const cloned = this.clonePeriodsForTarget(selectedPeriods, timePeriodType)
    if (onConfirm) {
      onConfirm({
        periods: cloned,
        targetDays: copyTargetDays,
        copyMode,
        targetContext,
      })
    }
  }

  render() {
    const { show, onHide, targetContext, timePeriodType, currentSettingName, selectedSettingId, settings = [], exceptions = [], weekdayRules = {} } = this.props
    const { copySourceSettingId, copySourceType, copySourceDay, copyWeekdaySourceDay, copySelectedPeriodIndices, copyTargetDays, copyMode } = this.state

    if (!show) return null

    const isHourly = timePeriodType === 'HOURLY'
    const previewPeriods = this.getSourcePeriods(copySourceSettingId, copySourceDay).rawPeriods || []
    const conflictWarnings = this.getCopyConflictWarnings()
    const availableExceptions = copySourceSettingId === '__CURRENT__'
      ? exceptions
      : (settings.find((s) => s.id === copySourceSettingId)?.date_exceptions || [])

    return (
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        data-cy="copy-day-modal"
        dialogClassName="whs-copy-modal-dialog"
        centered
      >
        <Modal.Header closeButton className="whs-modal-header">
          <Modal.Title className="whs-modal-title">Copy Time Periods</Modal.Title>
        </Modal.Header>

        {/* Primary Source Navigation Tabs */}
        <div className="whs-modal-tabs-header">
          <div className="whs-source-type-nav" data-cy="copy-source-type-nav">
            <button
              type="button"
              className={`whs-source-type-btn${copySourceType === 'WEEKDAY' ? ' active' : ''}`}
              onClick={() => this.changeCopySourceType('WEEKDAY')}
              data-cy="copy-source-type-weekday"
            >
              <CalendarTodayOutlinedIcon className="me-1 whs-tab-icon" />
              Weekdays
            </button>
            <button
              type="button"
              className={`whs-source-type-btn${copySourceType === 'EXCEPTION' ? ' active' : ''}`}
              onClick={() => this.changeCopySourceType('EXCEPTION')}
              disabled={availableExceptions.length === 0}
              data-cy="copy-source-type-exception"
            >
              <BeachAccessOutlinedIcon className="me-1 whs-tab-icon" />
              Exception Days ({availableExceptions.length})
            </button>
          </div>
        </div>

        <Modal.Body className="whs-modal-body">
          {/* Source Setting & Source Day in 2 Columns */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="whs-field-label">Source Setting</label>
              <Form.Select
                className="form-control form-control-input"
                value={copySourceSettingId}
                onChange={(e) => this.changeCopySourceSetting(e.target.value)}
                data-cy="copy-source-setting-select"
              >
                <option value="__CURRENT__">
                  {currentSettingName ? `${currentSettingName} (Current Setting)` : 'Current Setting'}
                </option>
                {settings
                  .filter((s) => s.id !== selectedSettingId)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{!s.is_active ? ' (Inactive)' : ''}
                    </option>
                  ))}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <label className="whs-field-label">
                {copySourceType === 'WEEKDAY' ? 'Source Day' : 'Source Exception'}
              </label>
              {copySourceType === 'WEEKDAY' ? (
                <Form.Select
                  className="form-control form-control-input"
                  value={copySourceDay}
                  onChange={(e) => this.changeCopySourceDay(e.target.value)}
                  data-cy="copy-source-day-select"
                >
                  {DAYS.map((d) => {
                    const count = this.getSourcePeriods(copySourceSettingId, d).rawPeriods.length
                    return (
                      <option key={d} value={d}>
                        {DAY_LABELS[d]} ({count} period{count !== 1 ? 's' : ''})
                      </option>
                    )
                  })}
                </Form.Select>
              ) : (
                <Form.Select
                  className="form-control form-control-input"
                  value={copySourceDay}
                  onChange={(e) => this.changeCopySourceDay(e.target.value)}
                  data-cy="copy-source-exc-select"
                >
                  {availableExceptions.map((exc, i) => {
                    const count = (exc.time_periods || exc.rules || []).length
                    const dateStr = exc.date ? (moment(exc.date).isValid() ? moment(exc.date).format('DD/MM/YYYY') : exc.date) : ''
                    const label = exc.name ? (dateStr ? `${exc.name} (${dateStr})` : exc.name) : (dateStr || `Exception ${i + 1}`)
                    return (
                      <option key={`exc_${i}`} value={`exc_${i}`}>
                        {label} — {count} period{count !== 1 ? 's' : ''}
                      </option>
                    )
                  })}
                </Form.Select>
              )}
            </div>
          </div>

          {/* Selectable Periods Preview */}
          <div className="whs-copy-preview-section">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="whs-field-label mb-0">Select Time Periods to Copy</label>
              {previewPeriods.length > 0 && (
                <span className="whs-period-select-count">
                  {copySelectedPeriodIndices.length} of {previewPeriods.length} selected
                </span>
              )}
            </div>

            {previewPeriods.length === 0 ? (
              <p className="whs-copy-empty-hint" data-cy="copy-preview-empty">
                No time periods configured for this source day
              </p>
            ) : (
              <div className="whs-table-wrap">
                <table className="whs-table whs-preview-table" data-cy="copy-preview-table">
                  <thead>
                    <tr>
                      <th className="whs-check-col">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={copySelectedPeriodIndices.length === previewPeriods.length && previewPeriods.length > 0}
                          onChange={() => this.toggleAllCopyPeriods(previewPeriods.length)}
                          title="Select / Deselect All"
                          data-cy="copy-period-select-all"
                        />
                      </th>
                      {isHourly ? <th>Hours</th> : <><th>Start</th><th>End</th></>}
                      <th>Type</th>
                      <th>Unpaid</th>
                      <th>Multiplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewPeriods.map((p, i) => {
                      const isSelected = copySelectedPeriodIndices.includes(i)
                      return (
                        <tr
                          key={i}
                          className={`whs-preview-row${isSelected ? ' is-selected' : ''}`}
                          onClick={() => this.toggleCopyPeriodIndex(i)}
                          data-cy={`copy-period-row-${i}`}
                        >
                          <td className="whs-check-col" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              onChange={() => this.toggleCopyPeriodIndex(i)}
                              data-cy={`copy-period-checkbox-${i}`}
                            />
                          </td>
                          {isHourly ? <td>{p.hours ?? '—'}</td> : <><td>{p.start || '—'}</td><td>{p.end || '—'}</td></>}
                          <td>{formatTypeLabel(p.time_type)}</td>
                          <td>{p.unpaid_allowance || 0}</td>
                          <td>{p.multiplier}×</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Copy Mode Selection (Replace vs Append) */}
          {previewPeriods.length > 0 && (
            <div className="whs-copy-mode-section">
              <label className="whs-field-label mb-1">Copy Action Mode</label>
              <div className="d-flex gap-3">
                <label className="whs-radio-label">
                  <input
                    type="radio"
                    name="copyMode"
                    className="form-check-input me-1"
                    checked={copyMode === 'REPLACE'}
                    onChange={() => this.setState({ copyMode: 'REPLACE' })}
                    data-cy="copy-mode-replace"
                  />
                  <span>Replace existing periods <span className="whs-mode-subtext">(Clean overwrite)</span></span>
                </label>
                <label className="whs-radio-label">
                  <input
                    type="radio"
                    name="copyMode"
                    className="form-check-input me-1"
                    checked={copyMode === 'APPEND'}
                    onChange={() => this.setState({ copyMode: 'APPEND' })}
                    data-cy="copy-mode-append"
                  />
                  <span>Append to existing periods</span>
                </label>
              </div>
            </div>
          )}

          {/* Conflict Warnings (if Append has overlaps) */}
          {conflictWarnings.length > 0 && (
            <div className="alert alert-warning mt-2 mb-0 py-2 px-3 whs-conflict-alert" data-cy="copy-conflict-alert">
              <strong className="d-flex align-items-center mb-1">
                <WarningAmberOutlinedIcon className="me-1 whs-warning-icon" />
                Time Period Conflicts Detected:
              </strong>
              <ul className="mb-1 ps-3">
                {conflictWarnings.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
              <div className="whs-conflict-hint">Choose <strong>Replace</strong> mode or adjust periods to resolve overlaps.</div>
            </div>
          )}

          {/* Target Days Section */}
          {targetContext === 'WEEKDAY' ? (
            <div className="whs-target-days-section">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="whs-field-label mb-0">Apply To (Target Days) <span className="text-danger">*</span></label>
                <div className="whs-target-shortcuts">
                  <button type="button" className="whs-shortcut-btn" onClick={this.selectAllTargetWeekdays}>Mon-Fri</button>
                  <button type="button" className="whs-shortcut-btn" onClick={this.selectAllTargetDays}>All 7 Days</button>
                  <button type="button" className="whs-shortcut-btn clear-btn" onClick={this.clearAllTargetDays}>Clear</button>
                </div>
              </div>
              <div className="whs-target-days-grid" data-cy="copy-target-days-list">
                {DAYS.map((d) => {
                  const isSource = copySourceSettingId === '__CURRENT__' && copyWeekdaySourceDay === d
                  const isChecked = !isSource && copyTargetDays.includes(d)
                  const count = (weekdayRules[d] || []).length
                  return (
                    <label
                      key={d}
                      className={`whs-target-day-chip${isChecked ? ' selected' : ''}${isSource ? ' is-source disabled' : ''}`}
                      data-cy={`target-day-chip-${d}`}
                      title={isSource ? 'Source day cannot be selected as target' : ''}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-1"
                        checked={isChecked}
                        disabled={isSource}
                        onChange={() => this.toggleCopyTargetDay(d)}
                        data-cy={`target-day-checkbox-${d}`}
                      />
                      <span>{DAY_SHORT[d]}</span>
                      <small className="whs-target-chip-count ms-1">
                        {isSource ? '(Source)' : `(${count})`}
                      </small>
                    </label>
                  )
                })}
              </div>
              {copyTargetDays.length === 0 && (
                <small className="text-danger d-block mt-1">Please select at least one target day to copy to.</small>
              )}
            </div>
          ) : (
            <div className="mt-3">
              <small className="text-muted">
                {targetContext === 'EXCEPTION_PANEL'
                  ? 'Periods will be applied to the exception you are currently editing.'
                  : 'Periods will be copied when you add or configure the exception.'}
              </small>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="whs-modal-footer">
          <Button variant="secondary" className="secondarybtn cancelbtn" onClick={onHide} data-cy="copy-day-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="primary"
            className="secondarybtn"
            disabled={
              previewPeriods.length === 0 ||
              copySelectedPeriodIndices.length === 0 ||
              (targetContext === 'WEEKDAY' && copyTargetDays.length === 0) ||
              conflictWarnings.length > 0
            }
            onClick={this.handleConfirm}
            data-cy="copy-day-confirm-btn"
          >
            {targetContext === 'WEEKDAY'
              ? `Apply to ${copyTargetDays.length} Day${copyTargetDays.length !== 1 ? 's' : ''}`
              : 'Apply to Exception'}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

CopyDayModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  targetContext: PropTypes.string,
  selectedDay: PropTypes.string,
  timePeriodType: PropTypes.string,
  currentSettingName: PropTypes.string,
  selectedSettingId: PropTypes.string,
  settings: PropTypes.array,
  weekdayRules: PropTypes.object,
  exceptions: PropTypes.array,
  eTimePeriods: PropTypes.array,
  selectedExcIdx: PropTypes.number,
}

export default CopyDayModal
